import type { TutorialStep } from '../../components/Tutorial/Tutorial';
import type { ApiRequestObject } from '../../lib/flotiq-client/types/api-request';
import type { TutorialLocalStorage } from '../../types/localStorage';
import type { HookOptions } from './hook-builders/types';

import { useCallback, useEffect, useState } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import {
  generateTutorialIndexPath,
  getElementBySelector,
} from '../../lib/helpers';

import useDebounceCallback from '../useDebounceCallback';
import { useTutorial } from './useTutorial';

const DEFAULT_STATE = {
  tutorials: [],
  tutorialsIndex: 0,
  tutorialsRun: false,
  updateStep: () => {},
  errors: null,
};

const DEFAULT_PARAMS = {};
const DEFAULT_OPTIONS = {};

type UpdateStepFn = (actionData: {
  action: string;
  index: number;
  status: string;
  type: string;
}) => void;

interface HookPageTutorialObjectResult {
  tutorials: TutorialStep[] | never[];
  tutorialsIndex: number;
  tutorialsRun: boolean;
  updateStep: UpdateStepFn;
  errors: any;
  reload: () => void;
}

type HookPageTutorialArrayResult = [
  tutorials: TutorialStep[] | never[],
  tutorialsIndex: number,
  tutorialsRun: boolean,
  updateStep: UpdateStepFn,
  errors: any,
  reload: () => void,
];

type HookPageTutorialResult = HookPageTutorialObjectResult &
  HookPageTutorialArrayResult;

export const usePageTutorial = (
  pageId: string,
  params: ApiRequestObject = DEFAULT_PARAMS,
  options: HookOptions = DEFAULT_OPTIONS,
) => {
  const { debounce = 0 } = options || {};
  const { tutorials: tutorialData, errors: tutorialDataErrors } = useTutorial(
    params,
    { ...options, staleTime: Infinity, gcTime: 0 },
  );

  const [tutorialsStorage, setTutorialsStorage] =
    useLocalStorageState<TutorialLocalStorage>('cms.tutorials');

  const [{ tutorials, tutorialsIndex, tutorialsRun, errors }, setResponseInfo] =
    useState<Omit<HookPageTutorialObjectResult, 'reload'>>(DEFAULT_STATE);

  useEffect(() => {
    if (tutorialDataErrors) {
      setResponseInfo({ ...DEFAULT_STATE, errors: tutorialDataErrors });
    }
  }, [tutorialDataErrors]);

  const reload = useCallback(() => {
    const filteredData = tutorialData?.filter((item) => item.page === pageId);

    if (
      JSON.parse(getEnv('VITE_ENABLE_TUTORIAL') || '') &&
      tutorialData.length > 0 &&
      !tutorialsStorage
    ) {
      setTutorialsStorage({ run: true });
    }

    if (
      options?.pause ||
      !tutorialData?.length ||
      !filteredData?.length ||
      !tutorialsStorage
    ) {
      setResponseInfo({ ...DEFAULT_STATE, tutorialsRun: false });
      return;
    }

    const getTutorialIndexFromLocal =
      tutorialsStorage?.['path']?.[pageId]?.['index'];

    // Case: No page path generate new path for all
    if (getTutorialIndexFromLocal === undefined) {
      const currentNewPath = generateTutorialIndexPath(tutorialData);
      setTutorialsStorage({ run: true, path: currentNewPath });
    }

    const stepIndex = getTutorialIndexFromLocal || 0;
    const target = filteredData[stepIndex]?.target || 'body';

    setResponseInfo({
      ...DEFAULT_STATE,
      tutorials: filteredData,
      tutorialsRun: false,
      tutorialsIndex: stepIndex,
    });

    getElementBySelector(target)
      .then(async (element) => {
        await getElementBySelector(
          '[data-testid=loader]:not(.invisible):not(.hidden):not(opacity-0)',
          false,
        );
        return element;
      })
      .then((element) =>
        setResponseInfo((prevState) => ({
          ...prevState,
          tutorialsRun: !!element,
        })),
      );
  }, [
    options?.pause,
    pageId,
    setTutorialsStorage,
    tutorialData,
    tutorialsStorage,
  ]);

  /**
   * Tutorial callback event handler
   */
  const updateStep = useCallback<UpdateStepFn>(
    (actionData) => {
      const { action, index, status, type } = actionData;
      const currentTutorialPage = tutorials?.[index]?.page;

      // Case: back from navigation to page with other tutorial
      if (tutorialsStorage?.path?.[currentTutorialPage]?.index !== index) {
        return false;
      }

      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      let currentIndex = nextIndex >= 0 ? nextIndex : 0;

      // Case: first element is not found and not to freeze on that part
      if (
        type === 'tour:start' ||
        (type === EVENTS.TARGET_NOT_FOUND && index === 0)
      ) {
        currentIndex = 1;
      }

      if (type === EVENTS.TARGET_NOT_FOUND || type === EVENTS.STEP_AFTER) {
        setTutorialsStorage((prevState) => {
          if (prevState?.path?.[currentTutorialPage]) {
            prevState.path[currentTutorialPage].index = currentIndex;
          } else if (prevState?.path) {
            prevState.path[currentTutorialPage] = { index: currentIndex };
          }
          return prevState;
        });
      }

      if (status === STATUS.SKIPPED) {
        setTutorialsStorage((prevState) => {
          return {
            ...prevState,
            run: false,
          };
        });
      }
    },
    [setTutorialsStorage, tutorials, tutorialsStorage],
  );

  const debouncedReload = useDebounceCallback(reload, debounce);
  useEffect(debouncedReload, [debouncedReload]);

  const result = [
    tutorials,
    tutorialsIndex,
    tutorialsRun,
    updateStep,
    errors,
    debouncedReload,
  ];

  Object.assign(result, {
    tutorials,
    tutorialsIndex,
    tutorialsRun,
    updateStep,
    errors,
    reload: debouncedReload,
  });

  return result as HookPageTutorialResult;
};
